import { pageSchema, userSummarySchema, type UserSummary } from '@wodi/shared';
import { useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';

import { api, ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import { RoleUpdatesNotice } from '../settings/role-updates-notice.tsx';
import { OpenTwoFactorResets } from '../settings/user-security.tsx';
import { SettingsPage } from './settings-nav.tsx';

const userPage = pageSchema(userSummarySchema);

function rolesText(user: UserSummary): string {
  if (user.facilityRoles.length === 0) return 'No roles';
  return user.facilityRoles.map((r) => `${r.facilityName}: ${r.roleName}`).join('; ');
}

export function UsersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'admin.users.read');

  const users = useInfiniteQuery({
    queryKey: ['users'],
    enabled: allowed,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api('GET', `/users?limit=50${pageParam === undefined ? '' : `&cursor=${pageParam}`}`, {
        schema: userPage,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Users" />
        <Notice tone="info">
          You don't have permission to see users. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }

  const items = users.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <SettingsPage title="Users" panel>
      <RoleUpdatesNotice />
      <OpenTwoFactorResets session={session} />
      {can(session, 'practitioners.manage') ? (
        <p>
          Clinicians&apos; cadre and licence details are kept in{' '}
          <Link to="/settings/practitioners">Practitioners</Link>.
        </p>
      ) : null}
      {users.isPending ? <LoadingBlock label="Loading users" rows={4} /> : null}
      {users.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void users.refetch()}>
              Try again
            </Button>
          }
        >
          {users.error instanceof ApiError
            ? users.error.message
            : "Users couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {users.isSuccess ? (
        <Table flat aria-label="Users">
          <TableHeader>
            <Column isRowHeader>Name</Column>
            <Column>Email</Column>
            <Column>Roles</Column>
            <Column>Status</Column>
          </TableHeader>
          <TableBody items={items} renderEmptyState={() => 'No users yet.'}>
            {(user) => (
              <Row id={user.id}>
                <Cell>
                  <Link to="/settings/users/$userId" params={{ userId: user.id }}>
                    {user.displayName}
                  </Link>
                </Cell>
                <Cell>{user.email}</Cell>
                <Cell>{rolesText(user)}</Cell>
                <Cell>
                  <Tag state={user.status === 'active' ? 'active' : 'neutral'}>
                    {user.status === 'active' ? 'Active' : 'Disabled'}
                  </Tag>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      {users.hasNextPage ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button
            isPending={users.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void users.fetchNextPage()}
          >
            Show more users
          </Button>
        </div>
      ) : null}
    </SettingsPage>
  );
}
